<template>
    <div class="uik-input__wrapper">
        <div v-if="label || infoText" class="uik-input__label-container">
            <span v-if="label" class="uik-content-title__wrapper uik-input__label">
                {{ label }}
                {{ (required ? '*':'') }}
            </span>
            <UikTooltip
                v-if="infoText"
                :text="infoText"
                :icon="infoIcon"
                :noPointerEvents="infoNoPointerEvents"
            >
                <span class="uik-input__info">?</span>
            </UikTooltip>
        </div>
        <div class="uik-input__inputWrapper">
            <slot></slot>
            <Uikon
                v-if="icon && !toggleVisibility"
                class="uik-input__iconWrapper"
                :class="{'uik-input__iconWrapperRight': iconPosition == 'right'}"
            >{{ icon }}</Uikon>
            <span
                v-if="toggleVisibility && type == 'password' && component !== 'textarea'"
                class="uik-input__toggleVisibility"
                :class="{'uik-input__toggleVisibility--on': inputType == 'text'}"
                @mousedown="value ? inputType='text':false"
                @mouseup="value ? inputType='password':false"
            >
                <Uikon
                    class="uik-input__iconWrapper uik-input__iconWrapperRight"
                >view_simple</Uikon>
            </span>
            <input
                v-if="component !== 'textarea'"
                ref="inputContent"
                :type="inputType"
                :placeholder="placeholder"
                :required="required"
                :value="value"
                :readonly="readonly"
                :disabled="disabled"
                :autocomplete="autocomplete"
                :autofocus="autofocus"
                :maxlength="maxlength"
                :min="min"
                :max="max"
                :step="step"
                :class="{
                    'uik-input__input': true,
                    'uik-input__errorHighlight': errorMessage !== '' ? true : false
                }"
                @input="emitWithConstraint('input', $event.target.value)"
                @change="emitWithConstraint('change', $event.target.value)"
                @blur="emitWithConstraint('blur', $event.target.value)"
                @focus="emitWithConstraint('focus', $event.target.value); selectContent()"
            />
            <textarea
                v-if="component === 'textarea'"
                ref="inputContent"
                :type="inputType"
                :placeholder="placeholder"
                :readonly="readonly"
                :rows="rows"
                :cols="cols"
                :required="required"
                :disabled="disabled"
                :autofocus="autofocus"
                :maxlength="maxlength"
                :class="{
                    'uik-input__input': true,
                    'uik-input__errorHighlight': errorMessage !== '' ? true : false
                }"
                :value="value"
                @input="$emit('input', $event.target.value)"
                @change="$emit('change', $event.target.value)"
                @blur="$emit('blur', $event.target.value)"
                @focus="$emit('focus', $event.target.value); selectContent()"
            />
        </div>
        <p v-if="errorMessage" class="uik-input__errorMessage">{{ errorMessage }}</p>
    </div>
</template>

<script>
export default {
    name: "UikInput",
    model: {
        prop: "value",
        event: "input"
    },
    props: {
        type: { type: String, default: "text" },
        label: { type: String, default: "" },
        value: { type: [String, Number], default: "" },
        placeholder: { type: String, default: "" },
        errorMessage: { type: String, default: "" },
        required: { type: Boolean, default: false },
        disabled: { type: Boolean, default: false },
        icon: { type: String, default: "" },
        iconPosition: { type: String, default: "" },
        toggleVisibility: { type: Boolean, default: false },
        component: { type: String, default: "input" },
        rows: { type: Number, default: undefined },
        cols: { type: Number, default: undefined },
        readonly: { type: Boolean, default: false },
        autocomplete: { type: Boolean, default: false },
        autofocus: { type: Boolean, default: false },
        maxlength: { type: Number, default: undefined },
        infoText: { type: String, default: "" },
        infoIcon: { type: String, default: "" },
        infoNoPointerEvents: { type: Boolean, default: false },
        /**
     * Min value of input
     * @type {Number}
     */
        min: { type: Number, default: undefined },
        /**
     * Max value of input
     * @type {Number}
     */
        max: { type: Number, default: undefined },
        /**
     * Slider input step
     *
     * @type {Boolean}
     */
        step: { type: Number, default: undefined },
        /**
     * Content is selected when component is clicked
     * @type {Number}
     */
        selectOnClick: { type: Boolean, default: false }
    },
    data () {
        return {
            inputType: ""
        }
    },
    created () {
        this.inputType = this.type
    },
    methods: {
        /**
       * Constrains number value to min and max
       *
       * @param {String} event - name of the emit event
       * @param {String, Number} val - value to emit
       *
       * @emit {String, Number} emits constrained number or unchanged string
       */
        emitWithConstraint (event, val) {
            if (this.type.toLowerCase() !== "number") {
                return this.$emit(event, val)
            }
            if (parseFloat(val) < this.min) {
                return this.$emit(event, this.min)
            }
            if (parseFloat(val) > this.max) {
                return this.$emit("input", this.max)
            }
            return this.$emit("input", val)
        },
        /**
       * Select content on click
       * highlight input content like CTRL+A
       */
        selectContent () {
            if (!this.selectOnClick) {
                return
            }
            try {
                this.$refs.inputContent.select()
            } catch (e) {
                let value = this.$refs.inputContent.value
                this.$refs.inputContent.setSelectionRange(0, value.length)
            }
        }
    }
}
</script>

<style lang="scss">
.uik-input__label-container {
    margin-top: 5px;
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    .uik-input__label {
        display: inline-block;
        margin-bottom: 8px;
    }
    .uik-input__info {
        font-weight: 500;
        font-size: 12px;
        color: $textSecondary;
        line-height: 1;
        border-radius: 50%;
        border: solid 1px $textLight;
        width: 18px;
        text-align: center;
        height: 18px;
        display: inline-flex;
        justify-content: center;
        align-items: center;
        margin: 0 2px 0 0;
        cursor: default;
        transition: border-color 0.15s;
        user-select: none;
        &:hover {
            border-color: $textSecondary;
        }
    }
}

.uik-input__inputWrapper {
    position: relative;
}

.uik-input__input {
    border: $borderDefault;
    border-radius: 4px;
    display: block;
    width: 100%;
    background: white;
    padding: 10px 15px;
    margin-bottom: 6px;
    transition: 0.2s all;
    outline: none;
    line-height: 1;
    & + * {
        margin-top: 10px;
    }
    &:disabled {
        background: rgba(255, 255, 255, 0.3);
        opacity: 0.6;
    }
    &:focus {
        border-color: $primary;
    }
    &::-webkit-input-placeholder,
    &::-moz-placeholder,
    &:-ms-input-placeholder,
    &:-moz-placeholder {
        color: $textSecondary;
    }
    font-size: 0.875rem;
    &.uik-input__errorHighlight {
        border-color: $error;
    }
}
.uik-input__input[type=number]::-webkit-inner-spin-button,
.uik-input__input[type=number]::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
}

textarea.uik-input__input {
    width: 100%;
    max-width: 1390px;
    padding: 10px 13px;
    outline: none;
    resize: vertical;
    display: block;
    min-height: 35px;
    max-height: 500px;
    margin-bottom: 6px;
    &::-webkit-scrollbar-track {
        display: initial;
    }
    &::-webkit-scrollbar {
        display: initial;
    }
    &[readonly]:focus {
        border-color: $borderColor;
    }
}

.uik-input__errorMessage {
    color: $error;
    line-height: 1.375rem;
}

.uik-input__iconWrapper {
    position: absolute;
    top: 50%;
    color: $borderColorDarker;
    transform: translateY(-50%);
    &:not(.uik-input__iconWrapperRight) {
        left: 12px;
        & + input {
            padding-left: 38px;
        }
    }
    &.uik-input__iconWrapperRight {
        left: auto;
        right: 12px;
        & + input {
            padding-right: 40px;
        }
    }
}

.uik-input__toggleVisibility {
    user-select: none;
    cursor: pointer;
    .uikon {
        transition: color 0.1s;
    }
    & + input {
        padding-right: 40px;
    }
}

.uik-input__toggleVisibility--on {
    .uikon {
        color: $textSecondary;
    }
}

.uik-input__clear {
    .uik-input__iconWrapper {
        &:not(.uik-input__iconWrapperRight) {
            left: 0;
            & + input {
                padding-left: 28px;
            }
        }
        &.uik-input__iconWrapperRight {
            right: 0;
            & + input {
                padding-right: 28px;
            }
        }
    }
    input {
        padding: 0;
        border: none !important;
        background: transparent;
    }
}

input:-webkit-autofill,
input:-webkit-autofill:hover,
input:-webkit-autofill:focus,
input:-webkit-autofill:active {
    transition: background-color 5500s ease-in-out 0s !important;
    color: $textBase !important;
    -webkit-text-fill-color: $textBase !important;
}
</style>
